import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/Sections";
import { IMAGES, MARKETS } from "@/components/site/data";

const pillars = [
  {
    title: "International Sourcing",
    text: "Identification and development of producers, manufacturers and brands across global sourcing markets.",
  },
  {
    title: "Procurement & Import",
    text: "Structured commercial procurement and importing of selected products into our destination markets.",
  },
  {
    title: "Market Development",
    text: "Destination-market strategy, channel development and long-term regional commercialisation.",
  },
  {
    title: "Private Label & Strategic Partnerships",
    text: "Private-label programmes, contract manufacturing and strategic commercial partnerships across our territories.",
  },
];

export default function About() {
  return (
    <>
      <PageHero overline="About RSC Imports" title="Building Cross-Border Trade Between Global Producers and High-Growth Markets.">
        <p>
          RSC Imports is an international sourcing, procurement and market-development company
          focused on identifying products with the potential to succeed across multiple consumer
          markets.
        </p>
        <p>
          Our commercial activities are centred around the Middle East, India and Southeast Asia.
          The company operates through a market-led sourcing model, assessing products on quality,
          commercial scalability, regulatory readiness, supply capability, consumer relevance and
          long-term market potential.
        </p>
      </PageHero>

      <section data-testid="about-approach-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <img
              src={IMAGES.agriculture}
              alt="Premium agricultural production"
              className="w-full h-[340px] md:h-[480px] object-cover border border-[#0D2B1E]/10"
            />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-our-approach">
              Our Approach
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413]">
              Procurement Beyond Traditional Trading.
            </h2>
            <div className="mt-6 h-px w-24 bg-[#BFA26F]" />
            <div className="mt-8 space-y-5 text-base md:text-lg font-light text-[#4A5D54] leading-relaxed" data-testid="approach-body">
              <p>
                RSC Imports does not approach international sourcing simply as a transaction between
                buyer and seller. We assess the complete commercial opportunity.
              </p>
              <p>
                This includes the producer, product, destination market, pricing structure,
                regulatory environment, distribution strategy and long-term scalability.
              </p>
              <p>
                Our objective is to develop sourcing relationships capable of supporting sustainable
                international growth.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-strategy-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-regional-strategy">
              Our Regional Strategy
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl mb-16">
              Three Regions. One Commercial Platform.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0D2B1E]/10" data-testid="strategy-grid">
            {MARKETS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.12} className="border-b border-r border-[#0D2B1E]/10">
                <div className="h-full bg-[#FBF9F6]/60 hover:bg-[#FBF9F6] transition-colors duration-300 group overflow-hidden">
                  <div className="h-40 overflow-hidden">
                    <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8 md:p-10">
                    <p className="font-display text-3xl font-medium text-[#BFA26F]/70 mb-6">{m.num}</p>
                    <h3 className="font-display text-2xl font-semibold text-[#111413] mb-4">{m.name}</h3>
                    <p className="text-sm md:text-base font-light text-[#4A5D54] leading-relaxed mb-8">{m.text}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#BFA26F] font-semibold">{m.footer}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-business-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#BFA26F] mb-4" data-testid="overline-our-business">
              Our Business
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight text-[#111413] max-w-3xl mb-16">
              Four Pillars of Activity.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#0D2B1E]/10" data-testid="business-pillars-grid">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1} className="border-b border-r border-[#0D2B1E]/10">
                <div className="p-8 md:p-10 h-full bg-white/60 hover:bg-white transition-colors duration-300">
                  <p className="font-display text-3xl font-medium text-[#BFA26F]/70 mb-6">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-[#111413] mb-4 leading-snug">{p.title}</h3>
                  <p className="text-sm font-light text-[#4A5D54] leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Work With RSC Imports."
        text="Our procurement team reviews new international sourcing opportunities throughout the year."
        buttonLabel="Partner With Us"
        to="/partner"
      />
    </>
  );
}
